import { Fragment, useState, useEffect } from 'react';
import * as Yup from 'yup';
import { Formik } from 'formik';
import FileUpload from '../utils/file-upload';
import axios from "axios";

import { useDropzone } from 'react-dropzone';
import API_ROUTES from "../../../config/api";

const BankWelcome = ({ state, updateState, nextStep, previousStep, changeStage }) => {

  const [ibanError, setIbanError] = useState(false);

  const [fileError, setFileError] = useState(false);
  const {acceptedFiles, getRootProps, getInputProps} = useDropzone({
    maxFiles: 1,
  });

  useEffect(() => {
    if (acceptedFiles.length >= 1) {
      setFileError(false);
    }
  }, [acceptedFiles])

  return (
    <Formik
      initialValues={{
        account_number: state?.data?.bank_verification?.account_number || state?.store?.bank_verification?.account_number || '',
        sort_code: state?.data?.bank_verification?.sort_code || state?.store?.bank_verification?.sort_code || '',
      }}
      validationSchema={Yup.object().shape({
        account_number: Yup.string()
          .min(3, 'Account number is not long enough')
          .required('Account number is required'),
          sort_code: Yup.string()
          .matches(/^\d{2}-\d{2}-\d{2}$/, 'Sort code must be in the format XX-XX-XX')
          .required('Sort code is required'),
      })}
      onSubmit={async (values, {
        setSubmitting, setFieldError
      }) => {
        setFileError(false);
        setSubmitting(true);

        // Validate number of files
        if (acceptedFiles.length === 0) {
          setFileError(true);
          setSubmitting(false);

          return null;
        }

        // Validate the IBAN
        let iban = null;

        if (ibanError === false) {
          try {
            const ibanCheck = await axios.post(
              'https://nuapay.api.felloh.com/iban',
              {
                account_number: values.account_number,
                sort_code: values.sort_code,
              },
              {
                timeout: 10000,
              }
            );
  
            iban = ibanCheck.data.data.iban;
          } catch (error) {
            setFieldError('account_number', 'Account number is possibly invalid, please check and try again');
            setFieldError('sort_code', 'Account number is possibly invalid, please check and try again');
  
            setIbanError(true);
            setSubmitting(false);
            return null;
          }
        }

        try {
          if (ibanError === false) {
            await axios.post(API_ROUTES.onboarding['application:update'](state.store.company_id), {
              bank_account_number: values.account_number,
              bank_sort_code: values.sort_code,
              iban,
            });
          } else {
            await axios.post(API_ROUTES.onboarding['application:update'](state.store.company_id), {
              bank_account_number: values.account_number,
              bank_sort_code: values.sort_code,
              iban: 'GB60BUKB0000000000000',
            });
          }
  
          await FileUpload(
            state.store.company_id,
            acceptedFiles[0],
            'Bank Statement'
          );
  
          updateState('bank_verification', values);
        } catch (error) {
          console.log(error)
        }

        if (state?.data?.bank_options?.has_statements === false) {
          changeStage('people-with-significant-control');
          nextStep(null, 'posc-welcome');

          return;
        }

        nextStep();
      }}
    >
      {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          touched,
          values,
          setFieldValue,
        }) => (
          <Fragment>
          <form action="#" method="POST" onSubmit={handleSubmit}>

          <h1 className="text-2xl font-semibold">Bank Verification</h1>
          { ibanError === false && (
            <p className='mb-6'>Please provide the details of your trading account (where fees will be taken out of)</p>
          )}
          { ibanError === true && (
            <p className='mb-6 text-red-500'><strong>Please check your account number and sort code and try again</strong><br/> If you are sure they are correct, you can continue.</p>
          )}
          

            <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">

              <div className="sm:col-span-2 mb-2">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-state">
                  Account Number *
                </label>
                <div className="mt-2.5">
                  <input type="text"
                         name="account_number"
                         id="account_number"
                         disabled={isSubmitting}
                         autoComplete="organization"
                         placeholder={'e.g. 370323230'}
                         value={values.account_number}
                         onBlur={handleBlur}
                         onChange={handleChange}
                         className={`block appearance-none w-full rounded-md border border-gray-200 text-gray-900 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500${touched.account_number && errors.account_number ? ' border-red-500' : ''}`}
                  />
                  {touched.account_number && errors.account_number && <div className="field-error">{errors.account_number}</div>}
                </div>
              </div>

              <div className="sm:col-span-2 mb-2">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-state">
                  Sort Code *
                </label>
                <div className="mt-2.5">
                  <input type="text"
                         name="sort_code"
                         id="sort_code"
                         disabled={isSubmitting}
                         autoComplete="organization"
                         placeholder={'e.g. 20-96-89'}
                         value={values.sort_code}
                         onBlur={handleBlur}
                         onChange={handleChange}
                         className={`block appearance-none w-full rounded-md border border-gray-200 text-gray-900 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500${touched.sort_code && errors.sort_code ? ' border-red-500' : ''}`}
                  />
                  {touched.sort_code && errors.sort_code && <div className="field-error">{errors.sort_code}</div>}
                </div>
              </div>

              <div className="sm:col-span-2 mb-2">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-state">
                  Bank Statement | from Last 3 Months *
                </label>
                { acceptedFiles.length === 0 && (
                  <div {...getRootProps({className: `${ fileError === true ? 'border-red ' : '' }dropzone`})}>
                    <input {...getInputProps()} />
                    <p>Drag and drop or click here to upload file</p>
                  </div>
                )}
                <aside className="dropzone-file-list">
                  <ul>{ acceptedFiles.map(file => (
                    <li key={file.path}>
                      <span className="file-path">{file.path}</span>
                      <span className="file-size">{file.size} bytes</span>
                    </li>
                  )) }</ul>
                </aside>
                {fileError && <div className="field-error">You must include at least one copy of your bank statement</div>}
              </div>

            </div>

            <div className="mt-12">

              <button type="submit"
                      disabled={isSubmitting}
                      className="bg-purple-800 disabled:bg-purple-600 hover:bg-purple-900 text-white font-semibold rounded-md py-2 px-4 w-full">
                { isSubmitting && (
                  <Fragment>
                    <svg aria-hidden="true" role="status" className="inline w-4 h-4 mr-3 text-white animate-spin"
                         viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="#E5E7EB"/>
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentColor"/>
                    </svg>
                  </Fragment>
                )}
                { !isSubmitting && (
                  <Fragment>
                    Next
                  </Fragment>
                )}
              </button>
            </div>

            <div className="mt-4">
              <button type="button"
                      onClick={previousStep}
                      disabled={isSubmitting}
                      className="block w-full rounded-md bg-slate-300 px-3.5 py-2.5 text-center text-md font-semibold text-black shadow-sm hover:text-white hover:bg-slate-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-600">
                Previous
              </button>
            </div>

          </form>

        </Fragment>
      )}
    </Formik>

  );
}

export default BankWelcome;
